import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "icon" }
const _hoisted_2 = { class: "icon" }
const _hoisted_3 = { class: "icon" }
const _hoisted_4 = { class: "icon" }
const _hoisted_5 = { class: "icon" }
const _hoisted_6 = { class: "icon" }
const _hoisted_7 = { class: "icon" }
const _hoisted_8 = { key: 2 }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = {
  key: 0,
  class: "callout"
}
const _hoisted_11 = { class: "icon" }
const _hoisted_12 = { key: 1 }
const _hoisted_13 = { class: "icon" }
const _hoisted_14 = { class: "icon" }
const _hoisted_15 = { class: "icon" }
const _hoisted_16 = { class: "icon" }

import { Delete } from '@element-plus/icons-vue';
import type { FormInstance } from 'element-plus';
import { onMounted, PropType, reactive, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { Criterion, CriterionCombination, CriterionRule } from '@/Litigation/criterion.entity';
import CriterionService from '@/Litigation/criterion.service';
import RuleService from '@/Rule/rule.service';


export default /*#__PURE__*/_defineComponent({
  __name: 'CriterionForm',
  props: {
    litigationId: {
        type: String,
        required: true,
    },
    criteria: {
        type: Array as PropType<Criterion[]>,
        required: true,
    },
    criterionId: {
        type: String as PropType<string | null>,
        default: null,
    },
},
  emits: ['criterionSaved', 'criterionSavedAndNew'],
  setup(__props, { emit }) {

const props = __props;

const { t } = useI18n();



const { litigationId, criteria, criterionId } = toRefs(props);
const criterionFormRef = ref<FormInstance>();
const criterion = reactive<Criterion>({
    occurrences: 1,
    type: 'input',
    rules: [],
    combinations: [],
    aiConfig: {
        noise: {
            intensity: 0,
            lowerThreshold: 0,
            upperThreshold: 0,
        },
        monotony: {},
        monotoneAugmentationAmplitude: {},
        monotoneAugmentationMin: {},
        monotoneAugmentationMax: {},
        request: '',
        proposal: '',
        augmentation: {},
    },
});
const availableFunctions = ref<Record<string, { message: string }>>({});



const formatChoices = (choices: Record<number, string> | null): string => {
    if (choices === null) {
        return '';
    }

    return Object.keys(choices)
        .map((key) => {
            return `${key}: ${choices[Number(key)]}`;
        })
        .join('\n');
};

const formatSuggestions = (suggestions: string[] | null): string => {
    if (!suggestions || suggestions.length === 0) {
        return '';
    }

    return suggestions.join('\n');
};

const fetchRules = async (): Promise<void> => {
    const ruleService = new RuleService();
    const functions: Record<string, { message: string }> = {};
    (await ruleService.findAll()).forEach((rule) => {
        functions[rule.function] = { message: rule.description };
    });

    availableFunctions.value = functions;
};

const fetchCriterion = async (criterionId: string): Promise<void> => {
    const criterionService = new CriterionService();
    const retrievedCriterion = await criterionService.find(criterionId);

    Object.assign(criterion, retrievedCriterion, {
        choices: formatChoices(retrievedCriterion.choices),
        suggestions: formatSuggestions(retrievedCriterion.suggestions),
    });
};

const criterionFormRules = {
    code: [
        {
            type: 'string',
            required: true,
        },
        {
            pattern: /^[a-z][a-z0-9_]+[a-z0-9]$/,
            message: t('criterionForm.invalidFormat'),
        },
    ],
    choices: [
        {
            type: 'string',
            required: true,
        },
        {
            type: 'string',
            validator: (rule, value: string, callback: any) => {
                const lines = value.split('\n');
                const errors = lines.filter((line) => line.trim() !== '' && !/^\-?\d+\s*:\s*(?!-1$)\S/.test(line));

                callback(errors.length > 0 ? new Error(t('criterionForm.invalidFormat')) : undefined);
            },
        },
    ],
};

const validateExclusive = (rule: any, value: any, callback: any) => {
    const items = value.split(';');
    const errors = items.filter((item) => item.trim() !== '' && !/^\-?\d+$/.test(item));

    callback(errors.length > 0 ? new Error(t('criterionForm.invalidFormat')) : undefined);
};

const fetchUsedSections = (queryString: string, cb: any) => {
    const sections = [
        ...new Set(
            criteria.value
                .filter((criterion: Criterion) => criterion.section)
                .map((criterion: Criterion) => criterion.section),
        ),
    ];
    cb(sections.map((section) => ({ value: section })));
};

const fetchUsedCxSections = (queryString: string, cb: any) => {
    const sections = [
        ...new Set(
            criteria.value
                .filter((criterion: Criterion) => criterion.cxSection)
                .map((criterion: Criterion) => criterion.cxSection),
        ),
    ];
    cb(sections.map((section) => ({ value: section })));
};

const fixEmptyFields = (model: Criterion) => {
    const fixedModel: Criterion = model;

    Object.entries(fixedModel).forEach(([field, value]) => {
        if (value === '') {
            fixedModel[field] = null;
        }
    });

    return fixedModel;
};

const isCriterionDisplayed = (key: keyof Criterion): boolean => {
    return ['criterionId', 'rules', 'combinations', 'aiConfig'].includes(key) || document.getElementById(key) !== null;
};

const removeHiddenFields = (model: Criterion) => {
    const cleanedModel: Criterion = model;

    Object.keys(cleanedModel).forEach((field) => {
        if (!isCriterionDisplayed(field)) {
            cleanedModel[field] = null;
        }
    });

    return cleanedModel;
};

const save = (formEl: FormInstance) => {
    return new Promise((resolve) => {
        formEl.validate(async (valid, _fields) => {
            if (!valid) {
                return;
            }

            const criterionService = new CriterionService();
            const model = fixEmptyFields(removeHiddenFields(criterion));

            if (criterionId.value) {
                await criterionService.update(criterionId.value, model);
            } else {
                await criterionService.create(litigationId.value, model);
            }

            resolve(model);
        });
    });
};

const onSubmit = async (formEl: FormInstance | undefined) => {
    if (!formEl) {
        return;
    }

    const savedModel = await save(formEl);
    emit('criterionSaved', savedModel);
};

const onSubmitAndNew = async (formEl: FormInstance | undefined) => {
    if (!formEl) {
        return;
    }

    const savedModel = await save(formEl);
    emit('criterionSavedAndNew', savedModel);
};

const addRule = () => {
    criterion.rules = criterion.rules || [];
    criterion.rules.push({ function: '', parameters: [] } as CriterionRule);
};

const removeRule = (rule: CriterionRule) => {
    const index = criterion.rules.indexOf(rule);
    if (index !== -1) {
        criterion.rules.splice(index, 1);
    }
};

const resetRule = (rule: CriterionRule) => {
    rule.parameters = [rule.parameters[0]];
};

const addCombination = () => {
    criterion.combinations = criterion.combinations || [];
    criterion.combinations.push({ min: 0, max: 0, exclusive: '' } as CriterionCombination);
};

const removeCombination = (combination: CriterionCombination) => {
    const index = criterion.combinations.indexOf(combination);
    if (index !== -1) {
        criterion.combinations.splice(index, 1);
    }
};

onMounted(async () => {
    fetchRules();

    if (criterionId.value) {
        fetchCriterion(criterionId.value);
    }
});

return (_ctx: any,_cache: any) => {
  const _component_el_radio_button = _resolveComponent("el-radio-button")!
  const _component_el_radio_group = _resolveComponent("el-radio-group")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_el_tooltip = _resolveComponent("el-tooltip")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_input_number = _resolveComponent("el-input-number")!
  const _component_el_autocomplete = _resolveComponent("el-autocomplete")!
  const _component_el_switch = _resolveComponent("el-switch")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_space = _resolveComponent("el-space")!
  const _component_el_form = _resolveComponent("el-form")!

  return criterion
    ? (_openBlock(), _createBlock(_component_el_form, {
        key: 0,
        ref_key: "criterionFormRef",
        ref: criterionFormRef,
        model: criterion,
        rules: criterionFormRules,
        "label-width": "180px"
      }, {
        default: _withCtx(() => [
          _createElementVNode("fieldset", null, [
            _createElementVNode("legend", null, _toDisplayString(_ctx.$t('criterionForm.general')), 1),
            _createVNode(_component_el_row, null, {
              default: _withCtx(() => [
                _createVNode(_component_el_col, { span: 24 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_form_item, {
                      label: _unref(t)('criterionForm.type'),
                      prop: "type",
                      required: ""
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_radio_group, {
                          id: "type",
                          modelValue: criterion.type,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((criterion.type) = $event))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_radio_button, { label: "input" }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.input')), 1)
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_el_radio_button, { label: "output" }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.output')), 1)
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_el_radio_button, { label: "none" }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.none')), 1)
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["modelValue"])
                      ]),
                      _: 1
                    }, 8, ["label"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_el_row, { justify: "space-around" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_col, { span: 11 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_form_item, {
                      prop: "code",
                      required: ""
                    }, {
                      label: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.code')) + " ", 1),
                        _createVNode(_component_el_tooltip, {
                          content: _unref(t)('criterionForm.codeHelp'),
                          "raw-content": ""
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", _hoisted_1, [
                              _createVNode(_component_font_awesome_icon, { icon: "question-circle" })
                            ])
                          ]),
                          _: 1
                        }, 8, ["content"])
                      ]),
                      default: _withCtx(() => [
                        _createVNode(_component_el_input, {
                          id: "code",
                          modelValue: criterion.code,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((criterion.code) = $event))
                        }, {
                          prepend: _withCtx(() => [
                            _createVNode(_component_el_select, {
                              id: "occurrences",
                              modelValue: criterion.occurrences,
                              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((criterion.occurrences) = $event)),
                              style: {"width":"80px"}
                            }, {
                              default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList([...Array(30).keys()], (i) => {
                                  return (_openBlock(), _createBlock(_component_el_option, {
                                    key: i,
                                    label: i + 1 + ' x',
                                    value: i + 1
                                  }, null, 8, ["label", "value"]))
                                }), 128))
                              ]),
                              _: 1
                            }, 8, ["modelValue"])
                          ]),
                          _: 1
                        }, 8, ["modelValue"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_col, { span: 1 }),
                _createVNode(_component_el_col, { span: 12 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_form_item, {
                      prop: "title",
                      required: ""
                    }, {
                      label: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.label')) + " ", 1),
                        _createVNode(_component_el_tooltip, {
                          content: _unref(t)('criterionForm.warningLength')
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", _hoisted_2, [
                              _createVNode(_component_font_awesome_icon, { icon: "question-circle" })
                            ])
                          ]),
                          _: 1
                        }, 8, ["content"])
                      ]),
                      default: _withCtx(() => [
                        _createVNode(_component_el_input, {
                          id: "title",
                          modelValue: criterion.title,
                          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((criterion.title) = $event))
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_el_row, { justify: "space-around" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_col, { span: 12 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_form_item, {
                      label: _unref(t)('criterionForm.control'),
                      prop: "control",
                      required: ""
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_select, {
                          id: "control",
                          modelValue: criterion.control,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((criterion.control) = $event)),
                          clearable: ""
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_option, {
                              label: "",
                              value: ""
                            }),
                            (['input', 'output', 'none'].includes(criterion.type))
                              ? (_openBlock(), _createBlock(_component_el_option, {
                                  key: 0,
                                  label: _unref(t)('criterionForm.switch'),
                                  value: "switch"
                                }, null, 8, ["label"]))
                              : _createCommentVNode("", true),
                            (['input', 'output', 'none'].includes(criterion.type))
                              ? (_openBlock(), _createBlock(_component_el_option, {
                                  key: 1,
                                  label: _unref(t)('criterionForm.dropdownSingle'),
                                  value: "dropdown_single"
                                }, null, 8, ["label"]))
                              : _createCommentVNode("", true),
                            (['input', 'none'].includes(criterion.type))
                              ? (_openBlock(), _createBlock(_component_el_option, {
                                  key: 2,
                                  label: _unref(t)('criterionForm.dropdownMultiple'),
                                  value: "dropdown_multiple"
                                }, null, 8, ["label"]))
                              : _createCommentVNode("", true),
                            (['input', 'output', 'none'].includes(criterion.type))
                              ? (_openBlock(), _createBlock(_component_el_option, {
                                  key: 3,
                                  label: _unref(t)('criterionForm.integer'),
                                  value: "integer"
                                }, null, 8, ["label"]))
                              : _createCommentVNode("", true),
                            (['input', 'none'].includes(criterion.type))
                              ? (_openBlock(), _createBlock(_component_el_option, {
                                  key: 4,
                                  label: _unref(t)('criterionForm.float'),
                                  value: "float"
                                }, null, 8, ["label"]))
                              : _createCommentVNode("", true),
                            (['input', 'none'].includes(criterion.type))
                              ? (_openBlock(), _createBlock(_component_el_option, {
                                  key: 5,
                                  label: _unref(t)('criterionForm.date'),
                                  value: "date"
                                }, null, 8, ["label"]))
                              : _createCommentVNode("", true),
                            (['input', 'none'].includes(criterion.type))
                              ? (_openBlock(), _createBlock(_component_el_option, {
                                  key: 6,
                                  label: _unref(t)('criterionForm.text'),
                                  value: "text"
                                }, null, 8, ["label"]))
                              : _createCommentVNode("", true),
                            (['input', 'none'].includes(criterion.type))
                              ? (_openBlock(), _createBlock(_component_el_option, {
                                  key: 7,
                                  label: _unref(t)('criterionForm.url'),
                                  value: "url"
                                }, null, 8, ["label"]))
                              : _createCommentVNode("", true)
                          ]),
                          _: 1
                        }, 8, ["modelValue"])
                      ]),
                      _: 1
                    }, 8, ["label"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_col, { span: 12 }, {
                  default: _withCtx(() => [
                    (['integer', 'float'].includes(criterion.control))
                      ? (_openBlock(), _createBlock(_component_el_form_item, {
                          key: 0,
                          label: _unref(t)('criterionForm.unit'),
                          prop: "unit"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_select, {
                              id: "unit",
                              modelValue: criterion.unit,
                              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((criterion.unit) = $event)),
                              clearable: ""
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_el_option, {
                                  label: "",
                                  value: ""
                                }),
                                _createVNode(_component_el_option, {
                                  label: "%",
                                  value: "percentage"
                                }),
                                _createVNode(_component_el_option, {
                                  label: "€",
                                  value: "euros"
                                }),
                                _createVNode(_component_el_option, {
                                  label: "€ HT",
                                  value: "euros_ht"
                                }),
                                _createVNode(_component_el_option, {
                                  label: "€ TTC",
                                  value: "euros_ttc"
                                }),
                                _createVNode(_component_el_option, {
                                  label: _unref(t)('criterionForm.days'),
                                  value: "day"
                                }, null, 8, ["label"]),
                                _createVNode(_component_el_option, {
                                  label: _unref(t)('criterionForm.months'),
                                  value: "month"
                                }, null, 8, ["label"]),
                                _createVNode(_component_el_option, {
                                  label: _unref(t)('criterionForm.years'),
                                  value: "year"
                                }, null, 8, ["label"]),
                                _createVNode(_component_el_option, {
                                  label: "m²",
                                  value: "m2"
                                }),
                                _createVNode(_component_el_option, {
                                  label: "km",
                                  value: "km"
                                })
                              ]),
                              _: 1
                            }, 8, ["modelValue"])
                          ]),
                          _: 1
                        }, 8, ["label"]))
                      : _createCommentVNode("", true),
                    (['integer', 'float', 'date'].includes(criterion.control))
                      ? (_openBlock(), _createBlock(_component_el_form_item, {
                          key: 1,
                          label: _unref(t)('criterionForm.minimum'),
                          prop: "min",
                          required: ""
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_input_number, {
                              id: "min",
                              modelValue: criterion.min,
                              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((criterion.min) = $event)),
                              min: criterion.control === 'date' ? 19000101 : undefined,
                              "controls-position": "right"
                            }, null, 8, ["modelValue", "min"])
                          ]),
                          _: 1
                        }, 8, ["label"]))
                      : _createCommentVNode("", true),
                    (['integer', 'float', 'date'].includes(criterion.control))
                      ? (_openBlock(), _createBlock(_component_el_form_item, {
                          key: 2,
                          label: _unref(t)('criterionForm.maximum'),
                          prop: "max",
                          required: criterion.control !== 'date'
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_input_number, {
                              id: "max",
                              modelValue: criterion.max,
                              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((criterion.max) = $event)),
                              min: criterion.control === 'date' ? 19000101 : undefined,
                              "controls-position": "right"
                            }, null, 8, ["modelValue", "min"]),
                            (criterion.control === 'date')
                              ? (_openBlock(), _createBlock(_component_el_tooltip, {
                                  key: 0,
                                  content: _unref(t)('criterionForm.warningDate')
                                }, {
                                  default: _withCtx(() => [
                                    _createElementVNode("span", _hoisted_3, [
                                      _createVNode(_component_font_awesome_icon, { icon: "question-circle" })
                                    ])
                                  ]),
                                  _: 1
                                }, 8, ["content"]))
                              : _createCommentVNode("", true)
                          ]),
                          _: 1
                        }, 8, ["label", "required"]))
                      : _createCommentVNode("", true),
                    (
                            ['input', 'output', 'none'].includes(criterion.type) &&
                            ['dropdown_single', 'dropdown_multiple'].includes(criterion.control)
                        )
                      ? (_openBlock(), _createBlock(_component_el_form_item, {
                          key: 3,
                          prop: "choices",
                          required: ""
                        }, {
                          label: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.choices')) + " ", 1),
                            _createVNode(_component_el_tooltip, {
                              content: _unref(t)('criterionForm.warningChoices'),
                              "raw-content": ""
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("span", _hoisted_4, [
                                  _createVNode(_component_font_awesome_icon, { icon: "question-circle" })
                                ])
                              ]),
                              _: 1
                            }, 8, ["content"])
                          ]),
                          default: _withCtx(() => [
                            _createVNode(_component_el_input, {
                              id: "choices",
                              modelValue: criterion.choices,
                              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((criterion.choices) = $event)),
                              type: "textarea",
                              autosize: { minRows: 3 }
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (['input', 'output', 'none'].includes(criterion.type) && criterion.control === 'text')
                      ? (_openBlock(), _createBlock(_component_el_form_item, {
                          key: 4,
                          prop: "suggestions"
                        }, {
                          label: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.suggestions')) + " ", 1),
                            _createVNode(_component_el_tooltip, {
                              content: _unref(t)('criterionForm.warningSuggestions'),
                              "raw-content": ""
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("span", _hoisted_5, [
                                  _createVNode(_component_font_awesome_icon, { icon: "question-circle" })
                                ])
                              ]),
                              _: 1
                            }, 8, ["content"])
                          ]),
                          default: _withCtx(() => [
                            _createVNode(_component_el_input, {
                              id: "suggestions",
                              modelValue: criterion.suggestions,
                              "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((criterion.suggestions) = $event)),
                              type: "textarea",
                              autosize: { minRows: 3 }
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_el_row, { justify: "space-around" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_col, { span: 12 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_form_item, {
                      prop: "section",
                      required: ""
                    }, {
                      label: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.section')) + " ", 1),
                        _createVNode(_component_el_tooltip, {
                          content: _unref(t)('criterionForm.warningSection'),
                          "raw-content": ""
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", _hoisted_6, [
                              _createVNode(_component_font_awesome_icon, { icon: "question-circle" })
                            ])
                          ]),
                          _: 1
                        }, 8, ["content"])
                      ]),
                      default: _withCtx(() => [
                        _createVNode(_component_el_autocomplete, {
                          id: "section",
                          modelValue: criterion.section,
                          "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((criterion.section) = $event)),
                          "fetch-suggestions": fetchUsedSections
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_col, { span: 12 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_form_item, {
                      label: _unref(t)('criterionForm.help'),
                      prop: "help"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_input, {
                          id: "help",
                          modelValue: criterion.help,
                          "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((criterion.help) = $event)),
                          type: "textarea",
                          autosize: { minRows: 3 }
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }, 8, ["label"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_el_row, null, {
              default: _withCtx(() => [
                _createVNode(_component_el_col, { span: 12 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_form_item, {
                      label: _unref(t)('criterionForm.required'),
                      prop: "required"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_switch, {
                          id: "required",
                          modelValue: criterion.required,
                          "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((criterion.required) = $event))
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }, 8, ["label"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _createElementVNode("fieldset", null, [
            _createElementVNode("legend", null, _toDisplayString(_ctx.$t('criterionForm.rules')), 1),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(criterion.rules, (rule, ruleIndex) => {
              return (_openBlock(), _createBlock(_component_el_row, {
                key: ruleIndex,
                justify: "space-around"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_col, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_space, {
                        direction: "vertical",
                        alignment: "left"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_form_item, {
                            prop: 'rules.' + ruleIndex,
                            required: ""
                          }, {
                            label: _withCtx(() => [
                              _createTextVNode(" #" + _toDisplayString(ruleIndex + 1) + "  ", 1),
                              _createVNode(_component_el_button, {
                                type: "danger",
                                plain: "",
                                icon: _unref(Delete),
                                onClick: _withModifiers(($event: any) => (removeRule(rule)), ["prevent"])
                              }, null, 8, ["icon", "onClick"])
                            ]),
                            default: _withCtx(() => [
                              _createVNode(_component_el_select, {
                                id: 'rules.' + ruleIndex,
                                modelValue: rule.function,
                                "onUpdate:modelValue": ($event: any) => ((rule.function) = $event),
                                onChange: ($event: any) => (resetRule(rule))
                              }, {
                                default: _withCtx(() => [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableFunctions.value, ({ message }, availableFunction) => {
                                    return (_openBlock(), _createBlock(_component_el_option, {
                                      key: availableFunction,
                                      label: message,
                                      value: availableFunction
                                    }, null, 8, ["label", "value"]))
                                  }), 128))
                                ]),
                                _: 2
                              }, 1032, ["id", "modelValue", "onUpdate:modelValue", "onChange"])
                            ]),
                            _: 2
                          }, 1032, ["prop"]),
                          (rule.function)
                            ? (_openBlock(), _createBlock(_component_el_space, { key: 0 }, {
                                default: _withCtx(() => [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableFunctions.value[rule.function].message.split(
                                    /(#(?:criterion|value)#)/,
                                ), (part, partIndex) => {
                                    return (_openBlock(), _createElementBlock(_Fragment, { key: part }, [
                                      (part === '#criterion#')
                                        ? (_openBlock(), _createBlock(_component_el_form_item, {
                                            key: 0,
                                            prop: 'rules.' + ruleIndex + '.criterion.' + partIndex
                                          }, {
                                            default: _withCtx(() => [
                                              _createVNode(_component_el_select, {
                                                id: 'rules.' + ruleIndex + '.criterion.' + partIndex,
                                                modelValue: rule.parameters[(partIndex - 1) / 2],
                                                "onUpdate:modelValue": ($event: any) => ((rule.parameters[(partIndex - 1) / 2]) = $event),
                                                required: "",
                                                filterable: ""
                                              }, {
                                                default: _withCtx(() => [
                                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(criteria), (ruleCriterion) => {
                                                    return (_openBlock(), _createBlock(_component_el_option, {
                                                      key: ruleCriterion.criterionId,
                                                      label: ruleCriterion.code,
                                                      value: ruleCriterion.criterionId
                                                    }, {
                                                      default: _withCtx(() => [
                                                        _createTextVNode(_toDisplayString(ruleCriterion.section) + " > " + _toDisplayString(ruleCriterion.code), 1)
                                                      ]),
                                                      _: 2
                                                    }, 1032, ["label", "value"]))
                                                  }), 128))
                                                ]),
                                                _: 2
                                              }, 1032, ["id", "modelValue", "onUpdate:modelValue"])
                                            ]),
                                            _: 2
                                          }, 1032, ["prop"]))
                                        : (part === '#value#')
                                          ? (_openBlock(), _createBlock(_component_el_form_item, {
                                              key: 1,
                                              prop: 'rules.' + ruleIndex + '.value.' + partIndex
                                            }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_el_space, null, {
                                                  default: _withCtx(() => [
                                                    _createVNode(_component_el_tooltip, {
                                                      content: _unref(t)('criterionForm.ruleExample'),
                                                      "raw-content": ""
                                                    }, {
                                                      default: _withCtx(() => [
                                                        _createElementVNode("span", _hoisted_7, [
                                                          _createVNode(_component_font_awesome_icon, { icon: "question-circle" })
                                                        ])
                                                      ]),
                                                      _: 1
                                                    }, 8, ["content"]),
                                                    _createVNode(_component_el_input, {
                                                      id: 'rules.' + ruleIndex + '.value.' + partIndex,
                                                      modelValue: rule.parameters[(partIndex - 1) / 2],
                                                      "onUpdate:modelValue": ($event: any) => ((rule.parameters[(partIndex - 1) / 2]) = $event),
                                                      required: ""
                                                    }, null, 8, ["id", "modelValue", "onUpdate:modelValue"])
                                                  ]),
                                                  _: 2
                                                }, 1024)
                                              ]),
                                              _: 2
                                            }, 1032, ["prop"]))
                                          : (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(part), 1))
                                    ], 64))
                                  }), 128))
                                ]),
                                _: 2
                              }, 1024))
                            : _createCommentVNode("", true)
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 2
                  }, 1024)
                ]),
                _: 2
              }, 1024))
            }), 128)),
            _createVNode(_component_el_form_item, null, {
              default: _withCtx(() => [
                _createVNode(_component_el_button, { onClick: addRule }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.newRule')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          (criterion.control === 'dropdown_multiple')
            ? (_openBlock(), _createElementBlock("fieldset", _hoisted_9, [
                _createElementVNode("legend", null, _toDisplayString(_ctx.$t('criterionForm.combinations')), 1),
                (criterion.combinations && criterion.combinations.length > 1)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(_ctx.$t('criterionForm.combinationsWarning')), 1))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(criterion.combinations, (combination, combinationIndex) => {
                  return (_openBlock(), _createBlock(_component_el_row, {
                    key: combinationIndex,
                    justify: "space-around"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_col, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_space, {
                            direction: "vertical",
                            alignment: "left"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_el_space, null, {
                                default: _withCtx(() => [
                                  _createTextVNode(" #" + _toDisplayString(combinationIndex + 1) + "  ", 1),
                                  _createVNode(_component_el_button, {
                                    type: "danger",
                                    plain: "",
                                    icon: _unref(Delete),
                                    onClick: _withModifiers(($event: any) => (removeCombination(combination)), ["prevent"])
                                  }, null, 8, ["icon", "onClick"]),
                                  _createVNode(_component_el_form_item, {
                                    label: _unref(t)('criterionForm.minValue'),
                                    prop: 'combinations.' + combinationIndex + '.min',
                                    required: ""
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_el_input_number, {
                                        id: 'combinations.' + combinationIndex + '.min',
                                        modelValue: combination.min,
                                        "onUpdate:modelValue": ($event: any) => ((combination.min) = $event),
                                        "controls-position": "right",
                                        "step-strictly": "",
                                        required: ""
                                      }, null, 8, ["id", "modelValue", "onUpdate:modelValue"])
                                    ]),
                                    _: 2
                                  }, 1032, ["label", "prop"]),
                                  _createVNode(_component_el_form_item, {
                                    label: _unref(t)('criterionForm.maxValue'),
                                    prop: 'combinations.' + combinationIndex + '.max',
                                    required: ""
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_el_input_number, {
                                        id: 'combinations.' + combinationIndex + '.max',
                                        modelValue: combination.max,
                                        "onUpdate:modelValue": ($event: any) => ((combination.max) = $event),
                                        "controls-position": "right",
                                        "step-strictly": "",
                                        required: ""
                                      }, null, 8, ["id", "modelValue", "onUpdate:modelValue"])
                                    ]),
                                    _: 2
                                  }, 1032, ["label", "prop"]),
                                  _createVNode(_component_el_form_item, {
                                    prop: 'combinations.' + combinationIndex + '.exclusive',
                                    rules: {
                                    validator: validateExclusive,
                                }
                                  }, {
                                    label: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.orOnly')) + " ", 1),
                                      _createVNode(_component_el_tooltip, {
                                        content: _unref(t)('criterionForm.combinationExemple'),
                                        "raw-content": ""
                                      }, {
                                        default: _withCtx(() => [
                                          _createElementVNode("span", _hoisted_11, [
                                            _createVNode(_component_font_awesome_icon, { icon: "question-circle" })
                                          ])
                                        ]),
                                        _: 1
                                      }, 8, ["content"])
                                    ]),
                                    default: _withCtx(() => [
                                      _createVNode(_component_el_input, {
                                        id: 'combinations.' + combinationIndex + '.exclusive',
                                        modelValue: combination.exclusive,
                                        "onUpdate:modelValue": ($event: any) => ((combination.exclusive) = $event)
                                      }, null, 8, ["id", "modelValue", "onUpdate:modelValue"])
                                    ]),
                                    _: 2
                                  }, 1032, ["prop", "rules"])
                                ]),
                                _: 2
                              }, 1024)
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 2
                  }, 1024))
                }), 128)),
                _createVNode(_component_el_form_item, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_button, { onClick: addCombination }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.newCombination')), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("fieldset", null, [
            _createElementVNode("legend", null, _toDisplayString(_ctx.$t('criterionForm.litigation')), 1),
            _createVNode(_component_el_row, null, {
              default: _withCtx(() => [
                _createVNode(_component_el_col, { span: 12 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_form_item, {
                      label: _unref(t)('criterionForm.jpControl'),
                      prop: "cxJpControl"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_select, {
                          id: "cxJpControl",
                          modelValue: criterion.cxJpControl,
                          "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((criterion.cxJpControl) = $event)),
                          clearable: ""
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_option, {
                              label: "",
                              value: ""
                            }),
                            _createVNode(_component_el_option, {
                              label: _unref(t)('criterionForm.switch'),
                              value: "switch"
                            }, null, 8, ["label"]),
                            (['dropdown_single', 'dropdown_multiple'].includes(criterion.control))
                              ? (_openBlock(), _createBlock(_component_el_option, {
                                  key: 0,
                                  label: _unref(t)('criterionForm.dropdownSingle'),
                                  value: "dropdown_single"
                                }, null, 8, ["label"]))
                              : _createCommentVNode("", true),
                            (['dropdown_single', 'dropdown_multiple'].includes(criterion.control))
                              ? (_openBlock(), _createBlock(_component_el_option, {
                                  key: 1,
                                  label: _unref(t)('criterionForm.dropdownMultiple'),
                                  value: "dropdown_multiple"
                                }, null, 8, ["label"]))
                              : _createCommentVNode("", true),
                            (['integer', 'float'].includes(criterion.control))
                              ? (_openBlock(), _createBlock(_component_el_option, {
                                  key: 2,
                                  label: _unref(t)('criterionForm.range'),
                                  value: "range"
                                }, null, 8, ["label"]))
                              : _createCommentVNode("", true)
                          ]),
                          _: 1
                        }, 8, ["modelValue"])
                      ]),
                      _: 1
                    }, 8, ["label"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_col, { span: 12 }, {
                  default: _withCtx(() => [
                    (['input'].includes(criterion.type))
                      ? (_openBlock(), _createBlock(_component_el_form_item, {
                          key: 0,
                          label: _unref(t)('criterionForm.example'),
                          prop: "cxExample"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_input, {
                              id: "cxExample",
                              modelValue: criterion.cxExample,
                              "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((criterion.cxExample) = $event))
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        }, 8, ["label"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_el_row, { justify: "space-around" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_col, { span: 12 }, {
                  default: _withCtx(() => [
                    (['input'].includes(criterion.type))
                      ? (_openBlock(), _createBlock(_component_el_form_item, {
                          key: 0,
                          label: _unref(t)('criterionForm.section'),
                          prop: "cxSection",
                          required: ""
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_autocomplete, {
                              id: "cxSection",
                              modelValue: criterion.cxSection,
                              "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((criterion.cxSection) = $event)),
                              "fetch-suggestions": fetchUsedCxSections
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        }, 8, ["label"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_col, { span: 12 }, {
                  default: _withCtx(() => [
                    (['input'].includes(criterion.type))
                      ? (_openBlock(), _createBlock(_component_el_form_item, {
                          key: 0,
                          label: _unref(t)('criterionForm.help'),
                          prop: "cxHelp"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_input, {
                              id: "cxHelp",
                              modelValue: criterion.cxHelp,
                              "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((criterion.cxHelp) = $event)),
                              type: "textarea",
                              autosize: { minRows: 3 }
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        }, 8, ["label"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_el_row, { justify: "space-around" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_col, { span: 12 }, {
                  default: _withCtx(() => [
                    (['input'].includes(criterion.type))
                      ? (_openBlock(), _createBlock(_component_el_form_item, {
                          key: 0,
                          label: _unref(t)('criterionForm.quickEdit'),
                          prop: "cxQuickEdit"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_switch, {
                              id: "cxQuickEdit",
                              modelValue: criterion.cxQuickEdit,
                              "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((criterion.cxQuickEdit) = $event))
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        }, 8, ["label"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_col, { span: 12 }, {
                  default: _withCtx(() => [
                    (['input'].includes(criterion.type) && !['switch'].includes(criterion.control))
                      ? (_openBlock(), _createBlock(_component_el_form_item, {
                          key: 0,
                          label: _unref(t)('criterionForm.required'),
                          prop: "cxRequired"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_switch, {
                              id: "cxRequired",
                              modelValue: criterion.cxRequired,
                              "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((criterion.cxRequired) = $event))
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        }, 8, ["label"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          (criterion.aiConfig)
            ? (_openBlock(), _createElementBlock("fieldset", _hoisted_12, [
                _createElementVNode("legend", null, _toDisplayString(_ctx.$t('criterionForm.aiConfig')), 1),
                (['input', 'none'].includes(criterion.type) && ['integer', 'float'].includes(criterion.control))
                  ? (_openBlock(), _createBlock(_component_el_row, { key: 0 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_col, { span: 8 }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_form_item, {
                              label: _unref(t)('criterionForm.noiseIntensity'),
                              prop: "aiconfig_noise_intensity"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_el_input_number, {
                                  id: "aiconfig_noise_intensity",
                                  modelValue: criterion.aiConfig.noise.intensity,
                                  "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((criterion.aiConfig.noise.intensity) = $event)),
                                  precision: 2,
                                  min: 0,
                                  "controls-position": "right"
                                }, null, 8, ["modelValue"])
                              ]),
                              _: 1
                            }, 8, ["label"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_col, { span: 8 }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_form_item, {
                              label: _unref(t)('criterionForm.lowerThreshold'),
                              prop: "aiconfig_noise_lower_threshold"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_el_input_number, {
                                  id: "aiconfig_noise_lower_threshold",
                                  modelValue: criterion.aiConfig.noise.lowerThreshold,
                                  "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((criterion.aiConfig.noise.lowerThreshold) = $event)),
                                  min: 0,
                                  "controls-position": "right"
                                }, null, 8, ["modelValue"])
                              ]),
                              _: 1
                            }, 8, ["label"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_col, { span: 8 }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_form_item, {
                              label: _unref(t)('criterionForm.upperThreshold'),
                              prop: "aiconfig_noise_upper_threshold"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_el_input_number, {
                                  id: "aiconfig_noise_upper_threshold",
                                  modelValue: criterion.aiConfig.noise.upperThreshold,
                                  "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((criterion.aiConfig.noise.upperThreshold) = $event)),
                                  min: 0,
                                  "controls-position": "right"
                                }, null, 8, ["modelValue"])
                              ]),
                              _: 1
                            }, 8, ["label"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_el_row, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_col, { span: 24 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_form_item, {
                          label: _unref(t)('criterionForm.contextualData'),
                          prop: "aiconfig_contextual_data"
                        }, {
                          label: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.contextualData')) + " ", 1),
                            _createVNode(_component_el_tooltip, {
                              "raw-content": "",
                              content: _unref(t)('criterionForm.contextualDataHelp')
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("span", _hoisted_13, [
                                  _createVNode(_component_font_awesome_icon, { icon: "question-circle" })
                                ])
                              ]),
                              _: 1
                            }, 8, ["content"])
                          ]),
                          default: _withCtx(() => [
                            _createVNode(_component_el_input, {
                              id: "aiconfig_contextual_data",
                              modelValue: criterion.aiConfig.contextualData,
                              "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((criterion.aiConfig.contextualData) = $event)),
                              type: "textarea",
                              rows: 4,
                              placeholder: _unref(t)('criterionForm.contextualDataPlaceholder')
                            }, null, 8, ["modelValue", "placeholder"])
                          ]),
                          _: 1
                        }, 8, ["label"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                (['input', 'none'].includes(criterion.type))
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createElementVNode("strong", null, _toDisplayString(_ctx.$t('criterionForm.monotony')), 1),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(criteria).filter((v) => v.type === 'output'), (monotonyCriterion, monotonyIndex) => {
                        return (_openBlock(), _createBlock(_component_el_row, { key: monotonyIndex }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_col, { span: 6 }, {
                              default: _withCtx(() => [
                                _createVNode(_component_el_form_item, {
                                  label: monotonyCriterion.code,
                                  prop: 'aiconfig_monotony.' + monotonyIndex
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_el_select, {
                                      id: 'aiconfig_monotony.' + monotonyIndex,
                                      modelValue: criterion.aiConfig.monotony[monotonyCriterion.criterionId],
                                      "onUpdate:modelValue": ($event: any) => ((criterion.aiConfig.monotony[monotonyCriterion.criterionId]) = $event),
                                      placeholder: _unref(t)('criterionForm.notConsidered'),
                                      clearable: "",
                                      onChange: 
                                    (value) => {
                                        if (!['-1', '1'].includes(value)) {
                                            criterion.aiConfig.monotoneAugmentationAmplitude[
                                                monotonyCriterion.criterionId
                                            ] = '';
                                            criterion.aiConfig.monotoneAugmentationMin[monotonyCriterion.criterionId] =
                                                '';
                                            criterion.aiConfig.monotoneAugmentationMax[monotonyCriterion.criterionId] =
                                                '';
                                        }
                                    }
                                
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_el_option, {
                                          label: _unref(t)('criterionForm.descending'),
                                          value: "-1"
                                        }, null, 8, ["label"]),
                                        _createVNode(_component_el_option, {
                                          label: _unref(t)('criterionForm.neutral'),
                                          value: "0"
                                        }, null, 8, ["label"]),
                                        _createVNode(_component_el_option, {
                                          label: _unref(t)('criterionForm.ascending'),
                                          value: "1"
                                        }, null, 8, ["label"])
                                      ]),
                                      _: 2
                                    }, 1032, ["id", "modelValue", "onUpdate:modelValue", "placeholder", "onChange"])
                                  ]),
                                  _: 2
                                }, 1032, ["label", "prop"])
                              ]),
                              _: 2
                            }, 1024),
                            _createVNode(_component_el_col, { span: 6 }, {
                              default: _withCtx(() => [
                                (
                                ['switch', 'dropdown_single'].includes(monotonyCriterion.control) &&
                                ['-1', '1'].includes(criterion.aiConfig.monotony[monotonyCriterion.criterionId])
                            )
                                  ? (_openBlock(), _createBlock(_component_el_form_item, {
                                      key: 0,
                                      prop: 'aiconfig_monotone_augmentation_amplitude.' + monotonyIndex
                                    }, {
                                      label: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.monotoneAugmentationAmplitude')) + " ", 1),
                                        _createVNode(_component_el_tooltip, {
                                          "raw-content": "",
                                          content: _unref(t)('criterionForm.monotoneAugmentationAmplitudeHelp')
                                        }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("span", _hoisted_14, [
                                              _createVNode(_component_font_awesome_icon, { icon: "question-circle" })
                                            ])
                                          ]),
                                          _: 1
                                        }, 8, ["content"])
                                      ]),
                                      default: _withCtx(() => [
                                        _createVNode(_component_el_input, {
                                          id: 'aiconfig_monotone_augmentation_amplitude.' + monotonyIndex,
                                          modelValue: 
                                    criterion.aiConfig.monotoneAugmentationAmplitude[monotonyCriterion.criterionId]
                                ,
                                          "onUpdate:modelValue": ($event: any) => ((
                                    criterion.aiConfig.monotoneAugmentationAmplitude[monotonyCriterion.criterionId]
                                ) = $event),
                                          type: "number",
                                          step: "0.01",
                                          min: "0",
                                          "controls-position": "right"
                                        }, null, 8, ["id", "modelValue", "onUpdate:modelValue"])
                                      ]),
                                      _: 2
                                    }, 1032, ["prop"]))
                                  : _createCommentVNode("", true)
                              ]),
                              _: 2
                            }, 1024),
                            _createVNode(_component_el_col, { span: 6 }, {
                              default: _withCtx(() => [
                                (
                                ['switch', 'dropdown_single'].includes(monotonyCriterion.control) &&
                                ['-1', '1'].includes(criterion.aiConfig.monotony[monotonyCriterion.criterionId])
                            )
                                  ? (_openBlock(), _createBlock(_component_el_form_item, {
                                      key: 0,
                                      prop: 'aiconfig_monotone_augmentation_min.' + monotonyIndex
                                    }, {
                                      label: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.monotoneAugmentationMin')) + " ", 1),
                                        _createVNode(_component_el_tooltip, {
                                          content: _unref(t)('criterionForm.monotoneAugmentationMinHelp')
                                        }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("span", _hoisted_15, [
                                              _createVNode(_component_font_awesome_icon, { icon: "question-circle" })
                                            ])
                                          ]),
                                          _: 1
                                        }, 8, ["content"])
                                      ]),
                                      default: _withCtx(() => [
                                        _createVNode(_component_el_input, {
                                          id: 'aiconfig_monotone_augmentation_min.' + monotonyIndex,
                                          modelValue: criterion.aiConfig.monotoneAugmentationMin[monotonyCriterion.criterionId],
                                          "onUpdate:modelValue": ($event: any) => ((criterion.aiConfig.monotoneAugmentationMin[monotonyCriterion.criterionId]) = $event),
                                          type: "number",
                                          min: "0",
                                          "controls-position": "right"
                                        }, null, 8, ["id", "modelValue", "onUpdate:modelValue"])
                                      ]),
                                      _: 2
                                    }, 1032, ["prop"]))
                                  : _createCommentVNode("", true)
                              ]),
                              _: 2
                            }, 1024),
                            _createVNode(_component_el_col, { span: 6 }, {
                              default: _withCtx(() => [
                                (
                                ['switch', 'dropdown_single'].includes(monotonyCriterion.control) &&
                                ['-1', '1'].includes(criterion.aiConfig.monotony[monotonyCriterion.criterionId])
                            )
                                  ? (_openBlock(), _createBlock(_component_el_form_item, {
                                      key: 0,
                                      prop: 'aiconfig_monotone_augmentation_max.' + monotonyIndex
                                    }, {
                                      label: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.monotoneAugmentationMax')) + " ", 1),
                                        _createVNode(_component_el_tooltip, {
                                          content: _unref(t)('criterionForm.monotoneAugmentationMaxHelp')
                                        }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("span", _hoisted_16, [
                                              _createVNode(_component_font_awesome_icon, { icon: "question-circle" })
                                            ])
                                          ]),
                                          _: 1
                                        }, 8, ["content"])
                                      ]),
                                      default: _withCtx(() => [
                                        _createVNode(_component_el_input, {
                                          id: 'aiconfig_monotone_augmentation_max.' + monotonyIndex,
                                          modelValue: criterion.aiConfig.monotoneAugmentationMax[monotonyCriterion.criterionId],
                                          "onUpdate:modelValue": ($event: any) => ((criterion.aiConfig.monotoneAugmentationMax[monotonyCriterion.criterionId]) = $event),
                                          type: "number",
                                          min: "0",
                                          "controls-position": "right"
                                        }, null, 8, ["id", "modelValue", "onUpdate:modelValue"])
                                      ]),
                                      _: 2
                                    }, 1032, ["prop"]))
                                  : _createCommentVNode("", true)
                              ]),
                              _: 2
                            }, 1024)
                          ]),
                          _: 2
                        }, 1024))
                      }), 128))
                    ], 64))
                  : _createCommentVNode("", true),
                (['output'].includes(criterion.type))
                  ? (_openBlock(), _createBlock(_component_el_row, { key: 2 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_col, { span: 12 }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_form_item, {
                              label: _unref(t)('criterionForm.request'),
                              prop: "aiconfig_request"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_el_select, {
                                  id: "aiconfig_request",
                                  modelValue: criterion.aiConfig.request,
                                  "onUpdate:modelValue": _cache[23] || (_cache[23] = ($event: any) => ((criterion.aiConfig.request) = $event)),
                                  clearable: ""
                                }, {
                                  default: _withCtx(() => [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(criteria).filter((v) => v.type === 'input'), (v) => {
                                      return (_openBlock(), _createBlock(_component_el_option, {
                                        key: v.criterionId,
                                        label: v.code,
                                        value: v.criterionId
                                      }, null, 8, ["label", "value"]))
                                    }), 128))
                                  ]),
                                  _: 1
                                }, 8, ["modelValue"])
                              ]),
                              _: 1
                            }, 8, ["label"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_col, { span: 12 }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_form_item, {
                              label: _unref(t)('criterionForm.proposal'),
                              prop: "aiconfig_proposal"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_el_select, {
                                  id: "aiconfig_proposal",
                                  modelValue: criterion.aiConfig.proposal,
                                  "onUpdate:modelValue": _cache[24] || (_cache[24] = ($event: any) => ((criterion.aiConfig.proposal) = $event)),
                                  clearable: ""
                                }, {
                                  default: _withCtx(() => [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(criteria).filter((v) => v.type === 'input'), (v) => {
                                      return (_openBlock(), _createBlock(_component_el_option, {
                                        key: v.criterionId,
                                        label: v.code,
                                        value: v.criterionId
                                      }, null, 8, ["label", "value"]))
                                    }), 128))
                                  ]),
                                  _: 1
                                }, 8, ["modelValue"])
                              ]),
                              _: 1
                            }, 8, ["label"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          _createVNode(_component_el_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_el_col, { span: 24 }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_form_item, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_button, {
                        type: "primary",
                        onClick: _cache[25] || (_cache[25] = ($event: any) => (onSubmit(criterionFormRef.value)))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.submit')), 1)
                        ]),
                        _: 1
                      }),
                      (_unref(criterionId) === null)
                        ? (_openBlock(), _createBlock(_component_el_button, {
                            key: 0,
                            onClick: _cache[26] || (_cache[26] = ($event: any) => (onSubmitAndNew(criterionFormRef.value)))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t('criterionForm.submitAndNew')), 1)
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["model"]))
    : _createCommentVNode("", true)
}
}

})